/**
 * Organization Service
 * Handles organization management API calls (Super Admin only)
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { ORGANIZATION_ENDPOINTS, DEMO_DATA_ENDPOINTS, buildQueryString } from '../endpoints';

/**
 * @typedef {Object} CreateOrganizationDto
 * @property {string} name - Organization name (max 200 chars, required)
 * @property {string} [description] - Organization description (max 1000 chars)
 * @property {string} [logo] - Logo URL (max 500 chars)
 * @property {string} [contactEmail] - Contact email (max 200 chars)
 * @property {string} [contactPhone] - Contact phone (max 50 chars)
 * @property {string} [address] - Address (max 500 chars)
 * @property {string} adminEmail - Admin email (max 320 chars, required)
 * @property {string} adminFirstName - Admin first name (max 200 chars, required)
 * @property {string} adminLastName - Admin last name (max 200 chars, required)
 * @property {string} adminPassword - Admin password (6-100 chars, required)
 * @property {boolean} [isDemo] - Mark this org as a public demo (Demo Studio). Skips welcome/verification emails.
 * @property {string} [demoSlug] - Public demo slug, used in {website}/demos/{slug}
 * @property {string} [demoLandingJson] - JSON.stringify({ headline, subheadline, featuredShareTokens, demoLoginEmail, demoLoginPassword, published })
 */

/**
 * @typedef {Object} UpdateOrganizationDto
 * @property {string} [name] - Organization name (max 200 chars)
 * @property {string} [description] - Organization description (max 1000 chars)
 * @property {string} [logo] - Logo URL (max 500 chars)
 * @property {string} [contactEmail] - Contact email (max 200 chars)
 * @property {string} [contactPhone] - Contact phone (max 50 chars)
 * @property {string} [address] - Address (max 500 chars)
 * @property {boolean} [isActive] - Whether organization is active
 * @property {boolean} [isDemo] - Demo flag (Demo Studio). Does NOT change the demo login user's email/password.
 * @property {string} [demoSlug] - Public demo slug
 * @property {string} [demoLandingJson] - Demo landing config JSON (see CreateOrganizationDto)
 */

/**
 * @typedef {Object} OrganizationListParams
 * @property {boolean} [includeInactive] - Include inactive organizations (default: false)
 */

const organizationService = {
  /**
   * Get list of organizations
   * @param {OrganizationListParams} [params] - Query parameters
   * @returns {Promise<Array>} List of organizations
   */
  getOrganizations: async (params = {}) => {
    const queryString = buildQueryString(params);
    return apiRequest(() =>
      axiosInstance.get(`${ORGANIZATION_ENDPOINTS.LIST}${queryString}`)
    );
  },

  /**
   * Get organization by ID
   * @param {number} id - Organization ID
   * @returns {Promise<Object>} Organization data
   */
  getOrganizationById: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(ORGANIZATION_ENDPOINTS.GET_BY_ID(id))
    );
  },

  /**
   * Create new organization with admin user
   * @param {CreateOrganizationDto} organizationData - Organization data
   * @returns {Promise<Object>} Created organization
   */
  createOrganization: async (organizationData) => {
    return apiRequest(() =>
      axiosInstance.post(ORGANIZATION_ENDPOINTS.CREATE, organizationData)
    );
  },

  /**
   * Update existing organization
   * @param {number} id - Organization ID
   * @param {UpdateOrganizationDto} organizationData - Updated organization data
   * @returns {Promise<Object>} Updated organization
   */
  updateOrganization: async (id, organizationData) => {
    return apiRequest(() =>
      axiosInstance.put(ORGANIZATION_ENDPOINTS.UPDATE(id), organizationData)
    );
  },

  /**
   * Get per-demo analytics summary (view/launch/login totals), one row per demo org.
   * SuperAdmin only.
   * @returns {Promise<Array<{organizationId: number, totalViews: number, totalLaunches: number, totalLogins: number}>>}
   */
  getDemoStatsSummary: async () => {
    return apiRequest(() =>
      axiosInstance.get(ORGANIZATION_ENDPOINTS.DEMO_STATS_SUMMARY)
    );
  },

  /**
   * Get a single demo's analytics with a zero-filled daily time series.
   * @param {number} orgId - Demo organization ID
   * @param {number} [days=30] - Window length in days
   * @returns {Promise<{totalViews: number, totalLaunches: number, totalLogins: number, days: number, daily: Array<{date: string, views: number, launches: number, logins: number}>}>}
   */
  getDemoStats: async (orgId, days = 30) => {
    return apiRequest(() =>
      axiosInstance.get(ORGANIZATION_ENDPOINTS.DEMO_STATS(orgId, days))
    );
  },

  /**
   * Current builder + viewer credentials for a demo org (SuperAdmin only).
   * builderPassword is the shared config default, or null once a custom password was set (reset to reveal).
   * @param {number} id - Demo organization ID
   * @returns {Promise<{builderEmail: string, builderPassword: string|null, builderPasswordIsDefault: boolean, viewerEmail: string|null, viewerPassword: string|null}>}
   */
  getDemoCredentials: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(ORGANIZATION_ENDPOINTS.DEMO_CREDENTIALS(id))
    );
  },

  /**
   * Reset a demo org's builder password (SuperAdmin only). The returned builderPassword is shown ONCE —
   * it is never stored in plaintext.
   * @param {number} id - Demo organization ID
   * @param {string} [newPassword] - Optional explicit password; omitted ⇒ a random one is generated
   * @returns {Promise<Object>} Same shape as getDemoCredentials, with the fresh builderPassword
   */
  resetDemoBuilderPassword: async (id, newPassword) => {
    return apiRequest(() =>
      axiosInstance.post(ORGANIZATION_ENDPOINTS.DEMO_CREDENTIALS_RESET(id), {
        newPassword: newPassword || null,
      })
    );
  },

  /**
   * DNS status of a demo org's public subdomain, {slug}.aqrate.ca (SuperAdmin only).
   * @param {number} id - Demo organization ID
   * @returns {Promise<{hostname: string, configured: boolean, exists: boolean, proxied: boolean, message: string|null}>}
   */
  getDemoDomain: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(ORGANIZATION_ENDPOINTS.DEMO_DOMAIN(id))
    );
  },

  /**
   * Create (idempotently) the Cloudflare DNS record for a demo org's subdomain (SuperAdmin only).
   * @param {number} id - Demo organization ID
   * @returns {Promise<Object>} Same shape as getDemoDomain
   */
  provisionDemoDomain: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(ORGANIZATION_ENDPOINTS.DEMO_DOMAIN(id))
    );
  },

  /**
   * Delete organization
   * @param {number} id - Organization ID
   * @returns {Promise<void>}
   */
  deleteOrganization: async (id) => {
    return apiRequest(() =>
      axiosInstance.delete(ORGANIZATION_ENDPOINTS.DELETE(id))
    );
  },

  /**
   * Hard-delete a DEMO org and ALL its data (SuperAdmin only, TASK-0272). Cascade-removes its rows, purges its
   * S3 media, tears down its Cloudflare subdomain, deletes the org row. Demo-org-only (server-guarded). Irreversible.
   * @param {number} id - Demo organization ID
   * @returns {Promise<void>}
   */
  deleteDemo: async (id) => {
    return apiRequest(() =>
      axiosInstance.delete(DEMO_DATA_ENDPOINTS.DELETE_ORG(id))
    );
  },

  /**
   * Activate organization
   * @param {number} id - Organization ID
   * @returns {Promise<Object>} Updated organization
   */
  activateOrganization: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(ORGANIZATION_ENDPOINTS.ACTIVATE(id))
    );
  },

  /**
   * Deactivate organization
   * @param {number} id - Organization ID
   * @returns {Promise<Object>} Updated organization
   */
  deactivateOrganization: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(ORGANIZATION_ENDPOINTS.DEACTIVATE(id))
    );
  },

  /**
   * Helper: Validate organization creation data
   * @param {CreateOrganizationDto} organizationData - Organization data to validate
   * @returns {Object} Validation result { isValid: boolean, errors: string[] }
   */
  validateCreateData: (organizationData) => {
    const errors = [];

    // Required fields
    if (!organizationData.name || organizationData.name.trim() === '') {
      errors.push('Organization name is required');
    }

    if (!organizationData.adminEmail || organizationData.adminEmail.trim() === '') {
      errors.push('Admin email is required');
    }

    if (!organizationData.adminFirstName || organizationData.adminFirstName.trim() === '') {
      errors.push('Admin first name is required');
    }

    if (!organizationData.adminLastName || organizationData.adminLastName.trim() === '') {
      errors.push('Admin last name is required');
    }

    if (!organizationData.adminPassword || organizationData.adminPassword.trim() === '') {
      errors.push('Admin password is required');
    }

    // Length validations
    if (organizationData.name && organizationData.name.length > 200) {
      errors.push('Organization name must not exceed 200 characters');
    }

    if (organizationData.description && organizationData.description.length > 1000) {
      errors.push('Description must not exceed 1000 characters');
    }

    if (organizationData.logo && organizationData.logo.length > 500) {
      errors.push('Logo URL must not exceed 500 characters');
    }

    if (organizationData.contactEmail && organizationData.contactEmail.length > 200) {
      errors.push('Contact email must not exceed 200 characters');
    }

    if (organizationData.contactPhone && organizationData.contactPhone.length > 50) {
      errors.push('Contact phone must not exceed 50 characters');
    }

    if (organizationData.address && organizationData.address.length > 500) {
      errors.push('Address must not exceed 500 characters');
    }

    if (organizationData.adminEmail && organizationData.adminEmail.length > 320) {
      errors.push('Admin email must not exceed 320 characters');
    }

    if (organizationData.adminFirstName && organizationData.adminFirstName.length > 200) {
      errors.push('Admin first name must not exceed 200 characters');
    }

    if (organizationData.adminLastName && organizationData.adminLastName.length > 200) {
      errors.push('Admin last name must not exceed 200 characters');
    }

    if (organizationData.adminPassword) {
      if (organizationData.adminPassword.length < 6) {
        errors.push('Admin password must be at least 6 characters');
      }
      if (organizationData.adminPassword.length > 100) {
        errors.push('Admin password must not exceed 100 characters');
      }
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (organizationData.adminEmail && !emailRegex.test(organizationData.adminEmail)) {
      errors.push('Admin email must be a valid email address');
    }

    if (organizationData.contactEmail && !emailRegex.test(organizationData.contactEmail)) {
      errors.push('Contact email must be a valid email address');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Helper: Validate organization update data
   * @param {UpdateOrganizationDto} organizationData - Organization data to validate
   * @returns {Object} Validation result { isValid: boolean, errors: string[] }
   */
  validateUpdateData: (organizationData) => {
    const errors = [];

    // Length validations
    if (organizationData.name && organizationData.name.length > 200) {
      errors.push('Organization name must not exceed 200 characters');
    }

    if (organizationData.description && organizationData.description.length > 1000) {
      errors.push('Description must not exceed 1000 characters');
    }

    if (organizationData.logo && organizationData.logo.length > 500) {
      errors.push('Logo URL must not exceed 500 characters');
    }

    if (organizationData.contactEmail && organizationData.contactEmail.length > 200) {
      errors.push('Contact email must not exceed 200 characters');
    }

    if (organizationData.contactPhone && organizationData.contactPhone.length > 50) {
      errors.push('Contact phone must not exceed 50 characters');
    }

    if (organizationData.address && organizationData.address.length > 500) {
      errors.push('Address must not exceed 500 characters');
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (organizationData.contactEmail && !emailRegex.test(organizationData.contactEmail)) {
      errors.push('Contact email must be a valid email address');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Helper: Get organization display name
   * @param {Object} organization - Organization object
   * @returns {string} Display name
   */
  getDisplayName: (organization) => {
    return organization?.name || 'Unknown Organization';
  },

  /**
   * Helper: Format organization for display
   * @param {Object} organization - Organization object
   * @returns {Object} Formatted organization
   */
  formatForDisplay: (organization) => {
    return {
      ...organization,
      displayName: organization.name,
      statusLabel: organization.isActive ? 'Active' : 'Inactive',
      createdDate: organization.createdAt ? new Date(organization.createdAt).toLocaleDateString() : 'N/A',
    };
  },
};

export default organizationService;
